import { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronRight, ChevronDown, Folder, ClipboardList, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils/utils';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useTranslation } from 'react-i18next';

// Longueur maximale du nom du propriétaire affiché en fin de ligne (une ligne)
const OWNER_LABEL_MAX_LENGTH = 20;

function ownerLabel(ownedBy) {
    const owner = (ownedBy ?? '').trim();
    if (!owner) return null;
    if (owner.length <= OWNER_LABEL_MAX_LENGTH) return owner;
    return `${owner.slice(0, OWNER_LABEL_MAX_LENGTH - 1).trimEnd()}…`;
}

/**
 * Composant de sélection arborescente de dossiers et sous-dossiers
 * Permet de naviguer dans la GED et de sélectionner une destination
 *
 * @param {Object} props
 * @param {string} props.value - ID du dossier/sous-dossier sélectionné (uniq_id)
 * @param {Function} props.onChange - Callback appelé avec { type, uniq_id, path } lors de la sélection
 * @param {boolean} props.excludeProcedures - Exclure les rapports de procédure de la sélection (default: true)
 * @param {Set|Array} props.disabledIds - IDs (uniq_id) des nœuds à désactiver (non sélectionnables)
 * @param {string} props.className - Classes CSS additionnelles
 * @param {string} props.scrollHeight - Hauteur du ScrollArea (default: "h-[300px] md:h-[400px]")
 */
export function FolderTreeSelector({
    value,
    onChange,
    excludeProcedures = true,
    disabledIds,
    className,
    scrollHeight,
}) {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [folders, setFolders] = useState([]);
    const [subfolders, setSubfolders] = useState([]);
    const [expandedNodes, setExpandedNodes] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');

    // Charger l'arborescence au montage
    useEffect(() => {
        loadTree();
    }, []);

    const loadTree = async () => {
        setIsLoading(true);
        try {
            const response = await horodatyApi.getFoldersTree();
            const data = response.data;

            // Filtrer les dossiers non archivés
            const activeFolders = (data.folders || []).filter((f) => !f.is_archived);
            setFolders(activeFolders);

            // Filtrer les sous-dossiers (exclure les procédures et dossiers invitations si demandé)
            let activeSubfolders = data.subfolders || [];
            if (excludeProcedures) {
                const excludedTypes = ['procedure', 'invitations', 'folder_gallery'];
                activeSubfolders = activeSubfolders.filter((sf) => !excludedTypes.includes(sf.type?.toLowerCase()));
            }
            setSubfolders(activeSubfolders);
        } catch (err) {
            console.error('Erreur chargement arborescence:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // Construire l'arbre hiérarchique
    const tree = useMemo(() => {
        // Index des sous-dossiers par parent
        const subfoldersByFolder = {};
        const subfoldersByParent = {};

        subfolders.forEach((sf) => {
            if (sf.parent_subfolder_uniq_id) {
                // Sous-dossier d'un autre sous-dossier
                if (!subfoldersByParent[sf.parent_subfolder_uniq_id]) {
                    subfoldersByParent[sf.parent_subfolder_uniq_id] = [];
                }
                subfoldersByParent[sf.parent_subfolder_uniq_id].push(sf);
            } else {
                // Sous-dossier direct d'un dossier
                if (!subfoldersByFolder[sf.folder_uniq_id]) {
                    subfoldersByFolder[sf.folder_uniq_id] = [];
                }
                subfoldersByFolder[sf.folder_uniq_id].push(sf);
            }
        });

        // Fonction récursive pour construire les enfants d'un sous-dossier
        const buildSubfolderChildren = (parentUniqId, folderUniqId) => {
            const children = subfoldersByParent[parentUniqId] || [];
            return children.map((sf) => ({
                id: sf.uniq_id,
                name: sf.name,
                type: 'subfolder',
                subfolderType: sf.type,
                folderUniqId, // Garde la référence au dossier parent racine
                children: buildSubfolderChildren(sf.uniq_id, folderUniqId),
            }));
        };

        // Construire l'arbre des dossiers
        return folders.map((folder) => ({
            id: folder.uniq_id,
            name: folder.name,
            type: 'folder',
            ownerLabel: ownerLabel(folder.owned_by),
            children: (subfoldersByFolder[folder.uniq_id] || []).map((sf) => ({
                id: sf.uniq_id,
                name: sf.name,
                type: 'subfolder',
                subfolderType: sf.type,
                folderUniqId: folder.uniq_id, // Référence au dossier parent
                children: buildSubfolderChildren(sf.uniq_id, folder.uniq_id),
            })),
        }));
    }, [folders, subfolders]);

    // Filtrer l'arbre par recherche
    const filteredTree = useMemo(() => {
        if (!searchQuery.trim()) return tree;

        const query = searchQuery.toLowerCase();

        const filterNode = (node) => {
            const matches = node.name.toLowerCase().includes(query);
            const filteredChildren = node.children.map(filterNode).filter(Boolean);

            if (matches || filteredChildren.length > 0) {
                return {
                    ...node,
                    children: filteredChildren,
                };
            }
            return null;
        };

        return tree.map(filterNode).filter(Boolean);
    }, [tree, searchQuery]);

    // Calculer le chemin vers un nœud
    const getPathToNode = useCallback(
        (nodeId) => {
            const findPath = (nodes, targetId, currentPath = []) => {
                for (const node of nodes) {
                    const newPath = [...currentPath, node.name];
                    if (node.id === targetId) {
                        return newPath;
                    }
                    if (node.children?.length > 0) {
                        const result = findPath(node.children, targetId, newPath);
                        if (result) return result;
                    }
                }
                return null;
            };

            return findPath(tree, nodeId) || [];
        },
        [tree],
    );

    // Toggle expansion d'un nœud
    const toggleExpand = (nodeId) => {
        setExpandedNodes((prev) => {
            const next = new Set(prev);
            if (next.has(nodeId)) {
                next.delete(nodeId);
            } else {
                next.add(nodeId);
            }
            return next;
        });
    };

    // Sélectionner un nœud
    const handleSelect = (node) => {
        const path = getPathToNode(node.id);
        onChange({
            type: node.type,
            uniq_id: node.id,
            path: path.join(' / '),
            name: node.name,
            folderUniqId: node.type === 'subfolder' ? node.folderUniqId : node.id,
        });
    };

    // Composant récursif pour afficher un nœud
    const TreeNode = ({ node, depth = 0 }) => {
        const isExpanded = expandedNodes.has(node.id);
        const isSelected = value === node.id;
        const hasChildren = node.children?.length > 0;
        const isProcedure = node.subfolderType === 'procedure' || node.subfolderType === 'PROCEDURE';
        const isDisabled =
            disabledIds instanceof Set
                ? disabledIds.has(node.id)
                : Array.isArray(disabledIds)
                  ? disabledIds.includes(node.id)
                  : false;

        // Ne pas afficher les procédures si elles sont exclues
        if (excludeProcedures && isProcedure) return null;

        return (
            <div>
                <div
                    className={cn(
                        'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors',
                        isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-surface-active',
                        isSelected && !isDisabled && 'bg-secondary/10 border border-secondary',
                        depth > 0 && 'ml-4',
                    )}
                    style={{ paddingLeft: `${12 + depth * 16}px` }}
                    onClick={() => {
                        if (isDisabled) return;
                        handleSelect(node);
                        if (hasChildren) toggleExpand(node.id);
                    }}
                >
                    {/* Chevron pour expand/collapse */}
                    {hasChildren ? (
                        <Button
                            variant="plain"
                            color="neutral"
                            size="auto"
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(node.id);
                            }}
                            className="p-0.5 hover:bg-surface-active rounded h-auto w-auto"
                        >
                            {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                        </Button>
                    ) : (
                        <span className="w-5" />
                    )}

                    {/* Icône - même logique que FoldersColumns */}
                    {node.type === 'folder' ? (
                        <Folder className="h-4 w-4 text-muted-foreground" />
                    ) : isProcedure ? (
                        <ClipboardList className="h-4 w-4 text-muted-foreground" />
                    ) : (
                        <Folder className="h-4 w-4 text-muted-foreground" />
                    )}

                    {/* Nom */}
                    <span className="flex-1 text-sm text-foreground truncate">{node.name}</span>

                    {/* Propriétaire (dossiers) : chaque utilisateur a sa galerie « Photos non classées » */}
                    {node.ownerLabel && (
                        <span className="shrink-0 max-w-[150px] truncate whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                            {node.ownerLabel}
                        </span>
                    )}

                    {/* Checkmark si sélectionné */}
                    {isSelected && <Check className="h-4 w-4 text-secondary shrink-0" />}
                </div>

                {/* Enfants */}
                {hasChildren && isExpanded && (
                    <div>
                        {node.children.map((child) => (
                            <TreeNode key={child.id} node={child} depth={depth + 1} />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className={cn('flex items-center justify-center py-8', className)}>
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className={cn('space-y-3', className)}>
            {/* Barre de recherche */}
            <Input
                type="text"
                placeholder={t('common:rechercherUnDossier')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
            />

            {/* Arborescence */}
            <ScrollArea className={cn(scrollHeight || 'h-[300px] md:h-[400px]', 'border border-border rounded-lg p-2')}>
                {filteredTree.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                        {searchQuery ? t('common:aucunResultat') : t('common:aucunDossierDisponible')}
                    </div>
                ) : (
                    <div className="space-y-1">
                        {filteredTree.map((node) => (
                            <TreeNode key={node.id} node={node} />
                        ))}
                    </div>
                )}
            </ScrollArea>
        </div>
    );
}

/**
 * Affiche le chemin sélectionné sous forme de breadcrumb
 */
export function FolderPathDisplay({ path, className }) {
    if (!path) return null;

    return (
        <div className={cn('flex items-center gap-1 px-3 py-2 bg-surface-active rounded-lg text-sm', className)}>
            <Folder className="h-4 w-4 text-dir-report shrink-0" />
            <span className="text-foreground truncate">{path}</span>
        </div>
    );
}
